import React, { useEffect, useState } from "react";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Shield, Users, Globe2, Gamepad2, Crown, DollarSign, Activity, AlertCircle, CheckCircle2, Clock } from "lucide-react";

const ICONS = { signup: Users, match: Activity, premium: Crown, report: AlertCircle };

export default function AdminPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) {
    return (
      <AppShell>
        <PageContainer>
          <div className="text-center text-zinc-500 py-20">Loading admin panel...</div>
        </PageContainer>
      </AppShell>
    );
  }

  const series = stats.growth_series || [];
  const maxUsers = Math.max(1, ...series.map((d) => d.users || 0));
  const hasGrowth = series.some((d) => (d.users || 0) > 0);
  const events = stats.recent_events || [];
  const countries = stats.top_countries || [];
  const mrr = stats.mrr_eur ?? stats.mrr_usd ?? 0;

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={<><Shield size={12} className="inline mr-1" /> Admin</>}
          title="Command Center"
          subtitle="Live platform health and growth signals."
        />

        {/* KPI row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Kpi icon={Users} label="Total users" value={stats.users_total.toLocaleString()} />
          <Kpi icon={Activity} label="Online now" value={stats.users_online.toLocaleString()} highlight />
          <Kpi icon={Crown} label="Premium" value={stats.premium_subscribers.toLocaleString()} />
          <Kpi icon={DollarSign} label="MRR" value={`€${mrr.toLocaleString()}`} />
          <Kpi icon={Globe2} label="Countries" value={stats.countries_active} />
          <Kpi icon={Gamepad2} label="Games tracked" value={stats.games_tracked} />
          <Kpi icon={AlertCircle} label="Open tickets" value={stats.tickets_open} />
          <Kpi icon={CheckCircle2} label="Uptime 7d" value={`${stats.api_uptime_7d}%`} highlight />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Growth chart */}
          <section className="lg:col-span-2 rounded-2xl border border-white/5 bg-zinc-900/50 p-6" data-testid="admin-growth">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">Weekly growth</h3>
              <span className="text-xs text-zinc-500">Users · Matches</span>
            </div>
            <div className="mt-6 flex items-end gap-3 h-48">
              {hasGrowth ? (
                series.map((d) => {
                  const h = Math.max(10, ((d.users || 0) / maxUsers) * 100);
                  const m = Math.max(8, ((d.matches || 0) / maxUsers) * 100);
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex items-end justify-center gap-1 h-full">
                        <div className="w-3 bg-neon rounded-t" style={{ height: `${h}%` }} />
                        <div className="w-3 bg-blue-500/70 rounded-t" style={{ height: `${m}%` }} />
                      </div>
                      <span className="text-[11px] text-zinc-500 font-mono">{d.day}</span>
                    </div>
                  );
                })
              ) : (
                <div
                  className="w-full grid place-items-center text-center text-sm text-zinc-500"
                  data-testid="admin-growth-empty"
                >
                  <div>
                    <div className="font-display text-base font-bold text-zinc-300">No activity yet</div>
                    <div className="mt-1 text-xs">Signups will plot here automatically as pilots join.</div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Top countries */}
          <section className="rounded-2xl border border-white/5 bg-zinc-900/50 p-6" data-testid="admin-countries">
            <h3 className="font-display text-xl font-bold mb-4">Top countries</h3>
            {countries.length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center" data-testid="admin-countries-empty">
                No countries to rank yet.
              </div>
            ) : (
              <ul className="space-y-3">
                {countries.map((c) => {
                  const pct = (c.users / countries[0].users) * 100;
                  return (
                    <li key={c.country}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 font-semibold">
                          <span>{c.flag}</span> {c.country}
                        </span>
                        <span className="text-zinc-500 font-mono text-xs">{c.users.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-neon to-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Events */}
        <section className="mt-6 rounded-2xl border border-white/5 bg-zinc-900/50 p-6" data-testid="admin-events">
          <h3 className="font-display text-xl font-bold mb-4">Live events</h3>
          {events.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="admin-events-empty">
              No activity yet
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {events.map((e, i) => {
                const Icon = ICONS[e.type] || Activity;
                return (
                  <li key={i} className="py-3 flex items-center gap-3" data-testid={`admin-event-${i}`}>
                    <span className="w-8 h-8 rounded-lg bg-neon/10 border border-neon/20 text-neon grid place-items-center">
                      <Icon size={14} />
                    </span>
                    <span className="flex-1 text-sm text-zinc-200">{e.msg}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-500 font-mono">
                      <Clock size={12} /> {e.ago}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </PageContainer>
    </AppShell>
  );
}

function Kpi({ icon: Icon, label, value, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        highlight ? "border-neon/40 bg-neon/5" : "border-white/5 bg-zinc-900/60"
      }`}
    >
      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
        <Icon size={12} className="text-neon" />
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-black">{value}</div>
    </div>
  );
}
